import { Injectable, inject } from '@angular/core';
import { EnrollhereApiService } from '../enrollhere-api/enrollhere-api';
import {
  Consumer,
  Task,
  TaskAttempt,
  TaskRelation,
  TaskStatus,
  taskStatusToJSON,
  TaskNote,
  typeToJSON,
} from '@enrollhere/classes';
import { log } from '../utils/logger';
import {
  applyTaskAssignmentPool,
  readStringList,
} from '../utils/task-assignment';
import { LeadNoteDto } from './lead-note.service';
import {
  BULK_ACTION_REQUEST_TIMEOUT_MS,
  BulkActionTarget,
  toBulkSelectionBody,
  toPolicyBulkFilter,
} from '../utils/bulk-selection';

const logger = log.getLogger('task.service');

/**
 * The API sends work pools as flat `string[]`, but `Task.fromJSON` expects the
 * proto `StringListContainer`, so parse the task then restore the pool.
 */
function taskFromApi(raw: unknown): Task {
  const task = Task.fromJSON(raw);
  const source = (raw ?? {}) as {
    assignedToUserIds?: string[] | { list?: string[] };
    assignedToTags?: string[] | { list?: string[] };
  };
  return applyTaskAssignmentPool(task, {
    userIds: readStringList(source.assignedToUserIds),
    tags: readStringList(source.assignedToTags),
  });
}

export interface CategoryProgressRow {
  categoryId: string;
  total: number;
  pending: number;
  inProgress: number;
  completed: number;
  cancelled: number;
}

export interface CategoryProgressResponse {
  total: number;
  categories: CategoryProgressRow[];
}

export interface TaskTimelineDateBucket {
  date: string;
  count: number;
}

export interface TaskTimelineResponse {
  overdue: number;
  dueSoon: number;
  completionsByDay: TaskTimelineDateBucket[];
  openByDueDate: TaskTimelineDateBucket[];
}

export interface CategoryOutcomeEntry {
  code: string;
  count: number;
}

export interface CategoryOutcomeRow {
  categoryId: string;
  completed: number;
  outcomes: CategoryOutcomeEntry[];
}

export interface CategoryOutcomesResponse {
  categories: CategoryOutcomeRow[];
}

export interface TaskAggregationBucket {
  key: string;
  count: number;
}

export interface TaskAggregationResponse {
  total: number;
  aggregations: Record<
    string,
    TaskAggregationBucket[] | { min?: string | null; max?: string | null }
  >;
}

export interface TaskPreviewResult {
  name: string;
  description?: string;
  dueDate?: string;
  activeDate?: string;
  categoryId: string;
}

export interface TaskContactInfo {
  phone?: string;
  name?: string;
  email?: string;
  source: 'lead' | 'policy';
  sourceId: string;
  leadId?: string;
  policyId?: string;
  /**
   * True when the viewer is a real lead-list participant for this lead, so the
   * full lead/call page is safe to open. The in-task Open Lead panel loads
   * through the task-scoped endpoint and does not consult this flag.
   */
  canOpenLead?: boolean;
  /**
   * Server verdict on whether the in-task Open Lead panel would load this
   * lead (task-bound + pool/overseer while open, or standing access). Gates
   * the "Open Lead" affordance; only an explicit false hides it so older
   * servers keep the previous behavior.
   */
  canViewLead?: boolean;
}

export interface CreateTaskRequest {
  agencyId: string;
  name: string;
  categoryId: string;
  /** Work pool; unioned with the category's default assignees on the server. */
  assignedToUserIds?: string[];
  assignedToTags?: string[];
  dueDate?: string;
  description?: string;
  relations?: Array<{ id: string; type: string; name?: string }>;
  source?: string;
  activeDate?: string;
  dedupeId?: string;
}

export interface BulkCreateTaskRequest {
  task: Omit<CreateTaskRequest, 'relations'>;
  /**
   * Stable id for one user-initiated run, reused verbatim when the user
   * retries after a failure. The server derives a per-entity dedupeId from
   * it, so a replay returns the tasks the first attempt already created
   * instead of creating a second set.
   */
  requestId?: string;
  /** Decides which bulk endpoint the request goes to. */
  relationType: 'T_DIALER_LEAD' | 'T_POLICY' | 'T_CONSUMER';
  target: BulkActionTarget;
  /**
   * Private-lead agents resolve their selection from the agent-leads index.
   * Only meaningful for `relationType: 'T_DIALER_LEAD'`.
   */
  usePrivateLeads?: boolean;
  maxRecords?: number;
  /** Defaults to the server's per-item concurrency (10) when omitted; pass
   * `bulkConcurrencyForCount(selectionCount)` for larger selections. */
  concurrency?: number;
}

export interface BulkTaskOutcomeRow {
  id: string;
  /** Human-readable label (consumer name/phone) so callers can list
   * skipped/failed rows without a second lookup by id. */
  displayName?: string;
  outcome: 'created' | 'skipped' | 'failed';
  taskId?: string;
  skipReason?: string;
  error?: string;
}

/** `totalCreated + totalSkipped + totalFailed === results.length`. */
export interface BulkCreateTaskResult {
  totalMatched: number;
  totalCreated: number;
  totalSkipped: number;
  totalFailed: number;
  results: BulkTaskOutcomeRow[];
}

export interface UpdateTaskRequest {
  name?: string;
  /** Replaces the pool when provided; omit to leave assignment untouched. */
  assignedToUserIds?: string[];
  assignedToTags?: string[];
  dueDate?: string;
  description?: string;
  status?: string;
  categoryId?: string;
  completionNote?: string;
  activeDate?: string;
}

@Injectable({
  providedIn: 'root',
})
export class TaskService {
  private readonly api = inject(EnrollhereApiService);

  async listTasks(params: {
    agencyId: string;
    status?: TaskStatus[];
    categoryId?: string;
    assignedToUserId?: string;
    limit?: number;
    nextPageToken?: string;
  }): Promise<{ tasks: Task[]; total: number; nextPageToken?: string }> {
    try {
      const query = new URLSearchParams({
        agencyId: params.agencyId,
      });
      if (params.status?.length) {
        params.status.forEach(s => query.append('status', taskStatusToJSON(s)));
      }
      if (params.categoryId) query.append('categoryId', params.categoryId);
      if (params.assignedToUserId)
        query.append('assignedToUserId', params.assignedToUserId);
      if (params.limit) query.append('limit', String(params.limit));
      if (params.nextPageToken)
        query.append('nextPageToken', params.nextPageToken);

      const response = await this.api.get<{
        tasks: unknown[];
        total: number;
        nextPageToken?: string;
      }>(`task?${query}`);

      return {
        tasks: (response.tasks || []).map((t: unknown) => taskFromApi(t)),
        total: response.total || 0,
        nextPageToken: response.nextPageToken,
      };
    } catch (error) {
      logger.error('Error listing tasks', error);
      return { tasks: [], total: 0 };
    }
  }

  async searchTasks(params: {
    filter: Record<string, unknown>;
    orderBy?: { field: string; direction: 'asc' | 'desc' };
    size?: number;
    nextPageToken?: string;
  }): Promise<{ tasks: Task[]; total: number; nextPageToken?: string }> {
    try {
      const response = await this.api.post<{
        tasks: unknown[];
        total: number;
        nextPageToken?: string;
      }>('task/search', {
        filter: params.filter,
        orderBy: params.orderBy,
        size: params.size,
        nextPageToken: params.nextPageToken,
      });

      return {
        tasks: (response.tasks || []).map((t: unknown) => taskFromApi(t)),
        total: response.total || 0,
        nextPageToken: response.nextPageToken,
      };
    } catch (error) {
      logger.error('Error searching tasks', error);
      throw error;
    }
  }

  async previewTask(
    agencyId: string,
    categoryId: string,
    options?: { policyId?: string; variables?: Record<string, unknown> }
  ): Promise<TaskPreviewResult | null> {
    try {
      return await this.api.post<TaskPreviewResult>('task/preview', {
        agencyId,
        categoryId,
        policyId: options?.policyId,
        variables: options?.variables,
      });
    } catch (error) {
      logger.error('Error previewing task', error);
      return null;
    }
  }

  async createTask(request: CreateTaskRequest): Promise<Task> {
    const response = await this.api.post<{ task: unknown }>('task', request);
    if (!response.task) {
      throw new Error('Task create returned no task');
    }
    return taskFromApi(response.task);
  }

  /**
   * Creates one task per entity in `request.target`, each with its own
   * relation to that entity — the bulk equivalent of `createTask`, reusing
   * the same server-side task creation logic (category access, assignment
   * checks) for every entity. `target.mode === 'filter'` re-resolves the
   * matching set server-side rather than sending a page of ids.
   *
   * `relationType` picks the endpoint. Leads take `{ selection, task }`
   * with the shared bulk-selection contract; policies
   * take `{ filter, task }` where explicit ids ride inside the filter as
   * `policy-id` / `exclude-policy-id`, the same contract the policy book's
   * bulk status/owner actions use. `relationType` and `agencyId` are never
   * sent — the server derives the relation type from the route and stamps
   * each task with its own entity's agency.
   */
  async bulkCreateTask(
    request: BulkCreateTaskRequest
  ): Promise<BulkCreateTaskResult> {
    const {
      name,
      categoryId,
      assignedToUserIds,
      assignedToTags,
      dueDate,
      description,
      activeDate,
    } = request.task;
    const task = {
      name,
      categoryId,
      ...(assignedToUserIds?.length ? { assignedToUserIds } : {}),
      ...(assignedToTags?.length ? { assignedToTags } : {}),
      ...(dueDate === undefined ? {} : { dueDate }),
      ...(description === undefined ? {} : { description }),
      ...(activeDate === undefined ? {} : { activeDate }),
    };

    if (request.relationType === 'T_POLICY') {
      return this.api.post<BulkCreateTaskResult>(
        'policy/task/bulk',
        {
          filter: toPolicyBulkFilter(request.target),
          task,
          ...(request.requestId === undefined
            ? {}
            : { requestId: request.requestId }),
          ...(request.maxRecords === undefined
            ? {}
            : { maxPolicies: request.maxRecords }),
          ...(request.concurrency === undefined
            ? {}
            : { concurrency: request.concurrency }),
        },
        undefined,
        undefined,
        BULK_ACTION_REQUEST_TIMEOUT_MS
      );
    }

    if (request.relationType === 'T_CONSUMER') {
      const selection =
        request.target.mode === 'ids'
          ? {
              mode: 'ids' as const,
              consumerIds: request.target.ids,
              ...(request.maxRecords === undefined
                ? {}
                : { maxConsumers: request.maxRecords }),
              ...(request.concurrency === undefined
                ? {}
                : { concurrency: request.concurrency }),
            }
          : {
              mode: 'filter' as const,
              filter: Object.fromEntries(
                Object.entries(request.target.filter).filter(
                  ([key]) => key !== 'agencyId'
                )
              ),
              excludedIds: request.target.excludedIds,
              ...(request.maxRecords === undefined
                ? {}
                : { maxConsumers: request.maxRecords }),
              ...(request.concurrency === undefined
                ? {}
                : { concurrency: request.concurrency }),
            };
      return this.api.post<BulkCreateTaskResult>(
        'consumer/task/bulk',
        {
          agencyId: request.task.agencyId,
          selection,
          task,
          ...(request.requestId === undefined
            ? {}
            : { requestId: request.requestId }),
        },
        undefined,
        undefined,
        BULK_ACTION_REQUEST_TIMEOUT_MS
      );
    }

    return this.api.post<BulkCreateTaskResult>(
      'dialer/bulk/lead-task',
      {
        selection: toBulkSelectionBody({
          target: request.target,
          usePrivateLeads: request.usePrivateLeads,
          maxLeads: request.maxRecords,
          concurrency: request.concurrency,
        }),
        task,
        ...(request.requestId === undefined
          ? {}
          : { requestId: request.requestId }),
      },
      undefined,
      undefined,
      BULK_ACTION_REQUEST_TIMEOUT_MS
    );
  }

  async updateTask(taskId: string, body: UpdateTaskRequest): Promise<Task> {
    const response = await this.api.put<{ task: unknown }>(
      `task/${encodeURIComponent(taskId)}`,
      body
    );
    if (!response.task) {
      throw new Error('Task update returned no task');
    }
    return taskFromApi(response.task);
  }

  async deleteTask(taskId: string): Promise<void> {
    await this.api.delete(`task/${encodeURIComponent(taskId)}`);
  }

  async getTask(agencyId: string, taskId: string): Promise<Task | null> {
    try {
      const response = await this.api.get<{ task: unknown }>(
        `task/${encodeURIComponent(taskId)}?agencyId=${encodeURIComponent(agencyId)}`
      );
      return response.task ? taskFromApi(response.task) : null;
    } catch (error) {
      logger.error('Error fetching task', error);
      return null;
    }
  }

  async updateTaskStatus(
    agencyId: string,
    taskId: string,
    status: TaskStatus,
    completionNote?: string,
    outcomeCode?: string,
    outcomeData?: Record<string, unknown>,
    /** Call being handled, which lets its agent close a task assigned elsewhere. */
    callId?: string
  ): Promise<Task> {
    const body: Record<string, unknown> = {
      status: taskStatusToJSON(status),
    };
    if (completionNote) body['completionNote'] = completionNote;
    if (outcomeCode) body['outcomeCode'] = outcomeCode;
    if (outcomeData) body['outcomeData'] = outcomeData;
    if (callId) body['callId'] = callId;

    const response = await this.api.patch<{ task: unknown }>(
      `task/${encodeURIComponent(taskId)}/status?agencyId=${encodeURIComponent(agencyId)}`,
      body
    );
    if (!response.task) {
      throw new Error('Task status update returned no task');
    }
    return taskFromApi(response.task);
  }

  /**
   * Set the work pool. `userIds` and `tags` each replace their side of the
   * pool when provided; pass an empty array to clear that side.
   */
  async assignTask(
    agencyId: string,
    taskId: string,
    assignment: { userIds?: string[]; tags?: string[] }
  ): Promise<Task> {
    const response = await this.api.patch<{ task: unknown }>(
      `task/${encodeURIComponent(taskId)}/assign?agencyId=${encodeURIComponent(agencyId)}`,
      assignment
    );
    if (!response.task) {
      throw new Error('Task assign returned no task');
    }
    return taskFromApi(response.task);
  }

  async getTasksByRelation(
    agencyId: string,
    relationType: string,
    relationId: string
  ): Promise<Task[]> {
    try {
      const result = await this.searchTasks({
        filter: {
          agencyId: [agencyId],
          relationKeys: [`${relationType}:${relationId}`],
        },
        orderBy: { field: 'createdAt', direction: 'desc' },
        size: 200,
      });
      return result.tasks;
    } catch (error) {
      logger.error('Error fetching tasks by relation', error);
      return [];
    }
  }

  async getTaskNotes(agencyId: string, taskId: string): Promise<TaskNote[]> {
    try {
      const response = await this.api.get<{ notes: unknown[] }>(
        `task/${encodeURIComponent(taskId)}/notes?agencyId=${encodeURIComponent(agencyId)}`
      );
      return (response.notes || []).map((n: unknown) => TaskNote.fromJSON(n));
    } catch (error) {
      logger.error('Error fetching task notes', error);
      return [];
    }
  }

  async addTaskNote(
    agencyId: string,
    taskId: string,
    text: string
  ): Promise<TaskNote> {
    const response = await this.api.post<{ note: unknown }>(
      `task/${encodeURIComponent(taskId)}/notes?agencyId=${encodeURIComponent(agencyId)}`,
      { text }
    );
    if (!response.note) {
      throw new Error('Task note create returned no note');
    }
    return TaskNote.fromJSON(response.note);
  }

  async getCategoryOutcomes(params: {
    filter?: Record<string, unknown>;
  }): Promise<CategoryOutcomesResponse> {
    try {
      return await this.api.post<CategoryOutcomesResponse>(
        'task/category-outcomes',
        { filter: params.filter }
      );
    } catch (error) {
      logger.error('Error fetching category outcomes', error);
      return { categories: [] };
    }
  }

  async getTaskTimeline(params: {
    filter?: Record<string, unknown>;
  }): Promise<TaskTimelineResponse> {
    try {
      return await this.api.post<TaskTimelineResponse>('task/timeline', {
        filter: params.filter,
      });
    } catch (error) {
      logger.error('Error fetching task timeline', error);
      return {
        overdue: 0,
        dueSoon: 0,
        completionsByDay: [],
        openByDueDate: [],
      };
    }
  }

  async getCategoryProgress(params: {
    filter?: Record<string, unknown>;
  }): Promise<CategoryProgressResponse> {
    try {
      return await this.api.post<CategoryProgressResponse>(
        'task/category-progress',
        { filter: params.filter }
      );
    } catch (error) {
      logger.error('Error fetching category progress', error);
      return { total: 0, categories: [] };
    }
  }

  async aggregateTasks(params: {
    filter?: Record<string, unknown>;
    aggregations: Record<string, boolean>;
  }): Promise<TaskAggregationResponse> {
    try {
      return await this.api.post<TaskAggregationResponse>('task/aggregations', {
        filter: params.filter,
        aggregations: params.aggregations,
      });
    } catch (error) {
      logger.error('Error aggregating tasks', error);
      return { total: 0, aggregations: {} };
    }
  }

  /**
   * Category ids that have at least one task the caller can see, via the
   * auth-guarded task aggregation (byCategoryId). Applies to agents and
   * employees the same way list/search do (assignment, tags, overseer
   * visibility, agency view-all / TASKS_VIEW_ALL). Used to scope the category
   * filter to visible-task categories. Returns an empty set when nothing is
   * visible or the aggregation fails (aggregateTasks swallows errors) so
   * callers show no category options rather than the full list.
   */
  async getVisibleCategoryIds(agencyIds: string[]): Promise<Set<string>> {
    const ids = agencyIds.filter(Boolean);
    if (ids.length === 0) {
      return new Set();
    }
    const response = await this.aggregateTasks({
      filter: { agencyId: ids },
      aggregations: { byCategoryId: true },
    });
    const buckets = response.aggregations['byCategoryId'];
    const visible = new Set<string>();
    if (Array.isArray(buckets)) {
      for (const bucket of buckets) {
        if (bucket.key && bucket.count > 0) {
          visible.add(bucket.key);
        }
      }
    }
    return visible;
  }

  async logAttempt(
    taskId: string,
    attempt: {
      outcomeCode: string;
      resultDetail?: string;
      resultData?: Record<string, unknown>;
      executorType: string;
      executedByUserId?: string;
      durationSeconds?: number;
    }
  ): Promise<TaskAttempt> {
    const response = await this.api.post<{ attempt: unknown }>(
      `task/${encodeURIComponent(taskId)}/attempt`,
      attempt
    );
    if (!response.attempt) {
      throw new Error('Task attempt create returned no attempt');
    }
    return TaskAttempt.fromJSON(response.attempt);
  }

  async listAttempts(taskId: string): Promise<TaskAttempt[]> {
    try {
      const response = await this.api.get<{ attempts: unknown[] }>(
        `task/${encodeURIComponent(taskId)}/attempts`
      );
      return (response.attempts || []).map((a: unknown) =>
        TaskAttempt.fromJSON(a)
      );
    } catch (error) {
      logger.error('Error listing task attempts', error);
      return [];
    }
  }

  async aggregateAttempts(params: {
    filter?: Record<string, unknown>;
    aggregations: Record<string, boolean>;
  }): Promise<TaskAggregationResponse> {
    try {
      return await this.api.post<TaskAggregationResponse>(
        'task-attempt/aggregations',
        {
          filter: params.filter,
          aggregations: params.aggregations,
        }
      );
    } catch (error) {
      logger.error('Error aggregating task attempts', error);
      return { total: 0, aggregations: {} };
    }
  }

  async getTaskContactInfo(taskId: string): Promise<TaskContactInfo[]> {
    try {
      const response = await this.api.get<{ contacts: TaskContactInfo[] }>(
        `task/${encodeURIComponent(taskId)}/contact-info`
      );
      return response.contacts || [];
    } catch (error) {
      logger.error('Error fetching task contact info', error);
      return [];
    }
  }

  /**
   * The lead a task is about. Everyone in the task's work pool can read this
   * one lead while the task is open, which the lead route would refuse for a
   * pool member with no standing entitlement. Custom fields the caller would
   * not see as an agent participant are redacted server-side.
   */
  async getTaskLead(taskId: string, leadId?: string): Promise<Consumer> {
    const query = leadId ? `?leadId=${encodeURIComponent(leadId)}` : '';
    const response = await this.api.get<{ lead: unknown }>(
      `task/${encodeURIComponent(taskId)}/lead${query}`
    );
    return Consumer.fromJSON(response.lead ?? {});
  }

  getTaskLeadQueryOptions(taskId: string | undefined, leadId?: string) {
    return {
      queryKey: ['task-lead', taskId, leadId ?? 'default'] as const,
      queryFn: () => this.getTaskLead(taskId!, leadId),
      enabled: Boolean(taskId),
      staleTime: 1000 * 60 * 5,
    };
  }

  /**
   * Throws on failure (403/404/network) so the caller can render an error
   * state — a swallowed error here looks identical to "no notes yet".
   */
  async getTaskLeadNotes(
    taskId: string,
    leadId?: string
  ): Promise<LeadNoteDto[]> {
    const query = leadId ? `?leadId=${encodeURIComponent(leadId)}` : '';
    const response = await this.api.get<{ notes: LeadNoteDto[] }>(
      `task/${encodeURIComponent(taskId)}/lead/notes${query}`
    );
    return response.notes || [];
  }

  async createTaskLeadNote(
    taskId: string,
    content: string,
    leadId?: string
  ): Promise<LeadNoteDto | null> {
    try {
      return await this.api.post<LeadNoteDto>(
        `task/${encodeURIComponent(taskId)}/lead/note`,
        leadId ? { content, leadId } : { content }
      );
    } catch (error) {
      logger.error('Error creating task lead note', error);
      return null;
    }
  }

  buildRelationKey(relation: TaskRelation): string {
    return `${typeToJSON(relation.type)}:${relation.id}`;
  }

  buildRelationKeys(relations: TaskRelation[]): string[] {
    return relations.map(r => this.buildRelationKey(r));
  }
}
